import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Check, Eraser, FileText, ImageIcon, Loader2, Paperclip, Send, Sparkles, Trash2,
    User, X,
} from 'lucide-react';

import { ApiError, useApi } from '../../lib/api';
import type {
    ApiResponse, ProposalAttachment, ProposalMessage, ProposalRun, ProposalRunEvent,
} from '../../types';
import { RichText } from './RichText';

/**
 * Fil de discussion d'une proposition, et vue de la génération en train de se
 * faire.
 *
 * Corriger une proposition voulait dire en regénérer une depuis zéro : même
 * consigne, plan entièrement réécrit pour trois phrases à changer, plusieurs
 * minutes d'attente et un document qui différait partout. On dit désormais ce
 * qu'on veut changer, et la version suivante reprend la précédente sans y
 * toucher ailleurs.
 *
 * L'attente elle-même est traitée comme un moment de la conversation, et non
 * comme un écran vide. Les étapes s'inscrivent dans le fil au fur et à mesure,
 * le plan apparaît dès qu'il est écrit, titre par titre, et la zone de saisie
 * reste ouverte : une consigne envoyée pendant la génération est prise par
 * l'exécution en cours, à son prochain point de correction.
 *
 * Les échanges sont mis en forme, et une consigne peut porter des pièces. Une
 * capture de la slide qui déborde dit en une image ce qu'un paragraphe explique
 * mal, et le texte des documents joints part avec la consigne : une pièce
 * affichée que le modèle ne verrait pas serait pire que pas de pièce du tout.
 */

interface ProposalChatProps {
    proposalId: string;
    /** Exécution en cours, s'il y en a une. Le fil se rafraîchit alors. */
    activeRun?: ProposalRun;
    /** Étapes de l'exécution en cours, pour les mêler au fil. */
    events?: ProposalRunEvent[];
    /** Version la plus récente, pour dire ce qui sera repris. */
    currentVersion: number;
}

/** Ce qu'une consigne va déclencher, dit avant de l'envoyer. */
function attendu(activeRun: boolean, currentVersion: number): string {
    if (activeRun) {
        return 'La génération en cours prendra cette consigne à sa prochaine étape '
            + 'de correction.';
    }
    if (currentVersion > 0) {
        return `La version ${currentVersion} sera reprise et corrigée sur ce point, `
            + 'sans être réécrite.';
    }
    return 'Cette consigne sera jointe à la première génération.';
}

/** Titres du plan, dès qu'il est écrit. C'est la proposition qui apparaît. */
function planOutline(events: ProposalRunEvent[]): Array<{ type: string; titre: string }> {
    for (let index = events.length - 1; index >= 0; index -= 1) {
        const titres = events[index]?.payload?.titres;
        if (Array.isArray(titres) && titres.length > 0) return titres;
    }
    return [];
}

function heure(iso: string): string {
    const date = new Date(iso);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function poids(octets: number): string {
    if (octets < 1024) return `${octets} o`;
    if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
    return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}

/** Pièces d'un message, images affichées et documents nommés. */
function Pieces({ proposalId, pieces }: {
    proposalId: string;
    pieces: ProposalAttachment[];
}) {
    if (pieces.length === 0) return null;
    return (
        <div className="mt-2 flex flex-wrap gap-2">
            {pieces.map((piece) => {
                const url = `/api/proposals/${proposalId}/attachments/${piece.id}`;
                if (piece.format === 'image') {
                    return (
                        <a
                            key={piece.id}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={piece.filename}
                        >
                            <img
                                src={url}
                                alt={piece.filename}
                                className="max-h-40 rounded-lg border border-[var(--color-border)]"
                            />
                        </a>
                    );
                }
                return (
                    <a
                        key={piece.id}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border
                            border-[var(--color-border)] px-2 py-1 text-xs
                            text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
                    >
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                        <span className="max-w-48 truncate">{piece.filename}</span>
                        {/* Le nombre de caractères lus n'est pas un détail : un PDF
                            fait de captures d'écran en rend zéro, et le consultant
                            doit le savoir avant de s'étonner qu'il soit ignoré. */}
                        <span className="text-[var(--color-text-muted)]">
                            {piece.extractStatus === 'done'
                                ? `${piece.chars.toLocaleString('fr-FR')} car.`
                                : 'non lu'}
                        </span>
                    </a>
                );
            })}
        </div>
    );
}

export function ProposalChat({
    proposalId, activeRun, events = [], currentVersion,
}: ProposalChatProps) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState('');
    const [pieces, setPieces] = useState<File[]>([]);
    const [error, setError] = useState<string | null>(null);
    const bas = useRef<HTMLDivElement | null>(null);
    const champFichier = useRef<HTMLInputElement | null>(null);
    // Messages cochés en vue d'une suppression. Le mode n'existe que s'il y a une
    // sélection : un fil ordinaire n'a pas à s'encombrer de cases.
    const [choisis, setChoisis] = useState<Set<string>>(new Set());
    // Vider le fil est irréversible et ne se distingue pas d'un clic de trop :
    // le premier clic arme, le second exécute. Une boîte de dialogue ferait le
    // même travail en interrompant davantage.
    const [videAArmer, setVideAArmer] = useState(false);

    const { data } = useQuery({
        queryKey: ['proposal-messages', proposalId],
        queryFn: () => api.get<ApiResponse<ProposalMessage[]>>(
            `/api/proposals/${proposalId}/messages`,
        ),
        // Pendant une génération, le fil bouge sans action de l'utilisateur :
        // étapes, plan, compte rendu. Au repos, il ne bouge plus.
        refetchInterval: activeRun ? 2500 : false,
    });

    const messages = useMemo(() => data?.data || [], [data]);

    /**
     * Retire des messages du fil.
     *
     * Le fil est un espace de travail : une consigne mal formulée ou un essai
     * n'ont pas à rester sous les yeux. Le journal des exécutions, lui, n'est pas
     * touché, c'est lui qui garde la trace de ce qui a été fait.
     */
    const supprimer = useMutation({
        // Soit des messages désignés, soit le fil entier. Le serveur distingue
        // les deux intentions : énumérer tous les identifiants serait fragile,
        // une course entre la lecture et la suppression laissant des messages
        // derrière elle.
        mutationFn: (quoi: string[] | 'tout') => api.delete<ApiResponse<{ supprimes: number }>>(
            `/api/proposals/${proposalId}/messages`,
            quoi === 'tout' ? { tout: true } : { ids: quoi },
        ),
        onSuccess: () => {
            setChoisis(new Set());
            setVideAArmer(false);
            void queryClient.invalidateQueries({ queryKey: ['proposal-messages', proposalId] });
        },
        onError: (mutationError: ApiError) => {
            setVideAArmer(false);
            setError(mutationError.message);
        },
    });

    const basculer = (id: string) => setChoisis((precedents) => {
        const suivants = new Set(precedents);
        if (suivants.has(id)) suivants.delete(id);
        else suivants.add(id);
        return suivants;
    });
    const outline = planOutline(events);

    // Le fil défile vers le bas à chaque nouveau message, comme une discussion.
    useEffect(() => {
        bas.current?.scrollIntoView({ block: 'nearest' });
    }, [messages.length, events.length]);

    const envoyer = useMutation({
        mutationFn: () => {
            // Multipart dès qu'il y a une pièce : la route accepte les deux, et
            // envoyer un formulaire vide de fichiers ne coûte rien.
            const corps = new FormData();
            corps.append('content', draft.trim());
            for (const fichier of pieces) corps.append('attachments', fichier);
            // `upload` et non `post` : celui-ci impose un en-tête JSON, alors que
            // le navigateur doit composer lui-même la frontière multipart.
            return api.upload<ApiResponse<{
                runId: string | null; applique: string; explication: string;
            }>>(`/api/proposals/${proposalId}/messages`, corps);
        },
        onSuccess: () => {
            setDraft('');
            setPieces([]);
            setError(null);
            void queryClient.invalidateQueries({ queryKey: ['proposal-messages', proposalId] });
            void queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
        },
        onError: (mutationError: ApiError) => setError(mutationError.message),
    });

    // Une pièce seule est une consigne légitime : montrer la slide qui déborde
    // suffit à dire ce qui ne va pas.
    const vide = draft.trim().length < 3 && pieces.length === 0;

    return (
        <div className="card p-0">
            <div
                className="flex items-start justify-between gap-3 border-b
                    border-[var(--color-border)] px-4 py-3"
            >
                <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text)]">Discussion</p>
                    <p className="text-xs text-[var(--color-text-muted)]">
                        Dites ce que vous voulez changer. La proposition est reprise, pas
                        réécrite.
                    </p>
                </div>
                {messages.length > 0 && (
                    <div className="flex shrink-0 items-center gap-2">
                        {videAArmer && (
                            <button
                                className="text-xs text-[var(--color-text-secondary)]
                                    hover:text-[var(--color-text)]"
                                onClick={() => setVideAArmer(false)}
                            >
                                Annuler
                            </button>
                        )}
                        <button
                            className={[
                                'inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs',
                                videAArmer
                                    ? 'bg-[var(--color-error)] text-white'
                                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]',
                            ].join(' ')}
                            onClick={() => {
                                if (videAArmer) supprimer.mutate('tout');
                                else setVideAArmer(true);
                            }}
                            disabled={supprimer.isPending}
                            title={videAArmer
                                ? 'Cliquer à nouveau pour effacer définitivement'
                                : 'Effacer toute la discussion'}
                        >
                            {supprimer.isPending
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Eraser className="h-3.5 w-3.5" />}
                            {videAArmer
                                ? `Effacer ${messages.length} message${messages.length > 1 ? 's' : ''} ?`
                                : 'Tout effacer'}
                        </button>
                    </div>
                )}
            </div>
            {videAArmer && (
                <p
                    className="border-b border-[var(--color-border)] bg-[var(--color-warning-light)]
                        px-4 py-2 text-xs text-[var(--color-text)]"
                >
                    Le fil entier part, pièces jointes comprises, et les consignes encore en
                    attente sont annulées. Les versions déjà produites et le journal des
                    générations ne sont pas touchés.
                </p>
            )}

            {choisis.size > 0 && (
                <div
                    className="flex items-center justify-between gap-3 border-b
                        border-[var(--color-border)] bg-[var(--color-bg-hover)] px-4 py-2"
                >
                    <p className="text-xs text-[var(--color-text-secondary)]">
                        {choisis.size} message{choisis.size > 1 ? 's' : ''} sélectionné
                        {choisis.size > 1 ? 's' : ''}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
                            onClick={() => setChoisis(new Set())}
                        >
                            Annuler
                        </button>
                        <button
                            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs
                                text-[var(--color-error)] hover:bg-[var(--color-error-light)]"
                            onClick={() => supprimer.mutate([...choisis])}
                            disabled={supprimer.isPending}
                        >
                            {supprimer.isPending
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5" />}
                            Supprimer
                        </button>
                    </div>
                </div>
            )}

            <div className="max-h-[28rem] space-y-4 overflow-y-auto p-4">
                {messages.length === 0 && outline.length === 0 && (
                    <p className="text-xs text-[var(--color-text-muted)]">
                        Aucun échange pour le moment. Par exemple : « remplace la slide de
                        chiffrage par trois scénarios » ou « la section démarche est trop
                        générique, cite les livrables ».
                    </p>
                )}

                {messages.map((message) => {
                    const deNous = message.role === 'user';
                    const coche = choisis.has(message.id);
                    return (
                        // `group` pour ne révéler la corbeille qu'au survol : un
                        // fil qu'on lit n'a pas à afficher ses commandes.
                        <div
                            key={message.id}
                            className={[
                                'group flex gap-2.5 rounded-lg p-1 -m-1',
                                coche ? 'bg-[var(--color-primary-light)]' : '',
                            ].join(' ')}
                        >
                            <div
                                className={[
                                    'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                                    deNous
                                        ? 'bg-[var(--color-bg-hover)]'
                                        : 'bg-[var(--color-primary-light)]',
                                ].join(' ')}
                            >
                                {deNous
                                    ? <User className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
                                    : <Sparkles className="h-3.5 w-3.5 text-[var(--color-primary)]" />}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-[var(--color-text-muted)]">
                                    {deNous ? (message.authorName || 'Consultant') : 'Proposition'}
                                    {' · '}{heure(message.createdAt)}
                                    {message.status === 'pending' && ' · en attente de prise en compte'}
                                </p>
                                <RichText>{message.content}</RichText>
                                <Pieces
                                    proposalId={proposalId}
                                    pieces={message.attachments || []}
                                />
                            </div>
                            <div className="flex shrink-0 items-start gap-1">
                                <button
                                    onClick={() => basculer(message.id)}
                                    title={coche ? 'Retirer de la sélection' : 'Sélectionner'}
                                    className={[
                                        'flex h-5 w-5 items-center justify-center rounded border',
                                        coche
                                            ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                                            : 'border-[var(--color-border)] opacity-0 group-hover:opacity-100',
                                    ].join(' ')}
                                >
                                    {coche && <Check className="h-3 w-3" />}
                                </button>
                                <button
                                    onClick={() => supprimer.mutate([message.id])}
                                    disabled={supprimer.isPending}
                                    title="Supprimer ce message"
                                    className="rounded p-0.5 text-[var(--color-text-muted)] opacity-0
                                        hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-error)]
                                        group-hover:opacity-100"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    );
                })}

                {/* La proposition en train de se faire : les étapes, puis les
                    titres dès que le plan est écrit. Sans cela, plusieurs
                    minutes se passent sans que rien ne bouge. */}
                {activeRun && (
                    <div className="rounded-lg bg-[var(--color-bg-hover)] p-3">
                        <p className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
                            <Loader2 className="h-3 w-3 animate-spin text-[var(--color-primary)]" />
                            Génération en cours
                        </p>
                        {outline.length > 0 && (
                            <ol className="mt-1.5 space-y-0.5">
                                {outline.map((slide, index) => (
                                    <li
                                        key={`${index}-${slide.titre}`}
                                        className="truncate text-xs text-[var(--color-text-secondary)]"
                                    >
                                        {index + 1}. {slide.titre || `(${slide.type})`}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                )}
                <div ref={bas} />
            </div>

            <div className="border-t border-[var(--color-border)] p-4">
                {error && <p className="mb-2 text-xs text-[var(--color-error)]">{error}</p>}

                {pieces.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                        {pieces.map((fichier, rang) => (
                            <span
                                key={`${fichier.name}-${rang}`}
                                className="inline-flex items-center gap-1.5 rounded-lg border
                                    border-[var(--color-border)] px-2 py-1 text-xs
                                    text-[var(--color-text-secondary)]"
                            >
                                {fichier.type.startsWith('image/')
                                    ? <ImageIcon className="h-3.5 w-3.5" />
                                    : <FileText className="h-3.5 w-3.5" />}
                                <span className="max-w-40 truncate">{fichier.name}</span>
                                <span className="text-[var(--color-text-muted)]">
                                    {poids(fichier.size)}
                                </span>
                                <button
                                    onClick={() => setPieces(
                                        (liste) => liste.filter((_, index) => index !== rang),
                                    )}
                                    className="rounded p-0.5 hover:bg-[var(--color-bg-hover)]"
                                    title="Retirer"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <textarea
                    className="input min-h-20 text-sm"
                    value={draft}
                    placeholder="Ce qu'il faut changer. Vous pouvez joindre une capture ou un document."
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                        // Entrée envoie, Maj+Entrée passe à la ligne : c'est ce
                        // qu'on attend d'une zone de discussion.
                        if (event.key === 'Enter' && !event.shiftKey && !vide) {
                            event.preventDefault();
                            envoyer.mutate();
                        }
                    }}
                />

                <input
                    ref={champFichier}
                    type="file"
                    multiple
                    className="hidden"
                    accept=".pdf,.docx,.doc,.pptx,.xlsx,.txt,.csv,.md,.png,.jpg,.jpeg,.webp,.gif,.svg"
                    onChange={(event) => {
                        // Cinq pièces au plus, comme la route l'accepte.
                        const choisis = Array.from(event.target.files || []);
                        setPieces((liste) => [...liste, ...choisis].slice(0, 5));
                        event.target.value = '';
                    }}
                />

                <div className="mt-2 flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                        <button
                            className="rounded-lg p-1.5 text-[var(--color-text-secondary)]
                                hover:bg-[var(--color-bg-hover)]"
                            onClick={() => champFichier.current?.click()}
                            title="Joindre une image ou un document"
                        >
                            <Paperclip className="h-4 w-4" />
                        </button>
                        <p className="truncate text-xs text-[var(--color-text-muted)]">
                            {attendu(Boolean(activeRun), currentVersion)}
                        </p>
                    </div>
                    <button
                        className="btn-primary flex shrink-0 items-center gap-2 text-sm"
                        disabled={vide || envoyer.isPending}
                        onClick={() => envoyer.mutate()}
                    >
                        {envoyer.isPending
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Send className="h-4 w-4" />}
                        Envoyer
                    </button>
                </div>
            </div>
        </div>
    );
}
